import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  clearLookupCache,
  clearPlatformConfigurationCache,
  getLookupValues,
  getPlatformLookup,
  getPlatformBranding,
  getPublicBranding,
  listPlatformLookups,
  listPlatformConfiguration,
  updatePlatformBranding,
  upsertPlatformLookup,
  upsertPlatformConfiguration,
} from "@/lib/api";

export const PLATFORM_CONFIG_QUERY_KEY = ["platform-config"];
export const PLATFORM_BRANDING_QUERY_KEY = ["platform-branding"];
export const PLATFORM_LOOKUPS_QUERY_KEY = ["platform-lookups"];

export const usePlatformConfigurations = () =>
  useQuery({
    queryKey: PLATFORM_CONFIG_QUERY_KEY,
    queryFn: listPlatformConfiguration,
  });

export const usePublicBranding = () =>
  useQuery({
    queryKey: ["public-branding"],
    queryFn: getPublicBranding,
    staleTime: 60_000,
  });

export const usePlatformBranding = () =>
  useQuery({
    queryKey: PLATFORM_BRANDING_QUERY_KEY,
    queryFn: getPlatformBranding,
  });

export const usePlatformLookups = () =>
  useQuery({
    queryKey: PLATFORM_LOOKUPS_QUERY_KEY,
    queryFn: listPlatformLookups,
  });

export const usePlatformLookup = (key, { enabled = true } = {}) =>
  useQuery({
    queryKey: [...PLATFORM_LOOKUPS_QUERY_KEY, key],
    queryFn: () => getPlatformLookup(key),
    enabled: enabled && !!key,
  });

export const useLookupValues = (
  key,
  { includeInactive = false, enabled = true } = {}
) =>
  useQuery({
    queryKey: ["lookup-values", key, includeInactive],
    queryFn: () => getLookupValues(key, includeInactive),
    enabled: enabled && !!key,
    staleTime: 60_000,
  });

export const useUpsertPlatformConfiguration = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }) => upsertPlatformConfiguration(key, value),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PLATFORM_CONFIG_QUERY_KEY });
      qc.invalidateQueries({ queryKey: ["public-branding"] });
    },
  });
};

export const useClearPlatformConfigurationCache = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key } = {}) => clearPlatformConfigurationCache(key ?? null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PLATFORM_CONFIG_QUERY_KEY });
      qc.invalidateQueries({ queryKey: ["public-branding"] });
    },
  });
};

export const useUpdatePlatformBranding = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => updatePlatformBranding(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PLATFORM_BRANDING_QUERY_KEY });
      qc.invalidateQueries({ queryKey: ["public-branding"] });
      qc.invalidateQueries({ queryKey: PLATFORM_CONFIG_QUERY_KEY });
    },
  });
};

export const useUpsertPlatformLookup = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, payload }) => upsertPlatformLookup(key, payload),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: PLATFORM_LOOKUPS_QUERY_KEY });
      qc.invalidateQueries({ queryKey: ["lookup-values", variables?.key] });
    },
  });
};

export const useClearLookupCache = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key } = {}) => clearLookupCache(key ?? null),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: PLATFORM_LOOKUPS_QUERY_KEY });
      if (variables?.key) {
        qc.invalidateQueries({ queryKey: ["lookup-values", variables.key] });
      } else {
        qc.invalidateQueries({ queryKey: ["lookup-values"] });
      }
    },
  });
};
